import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/feature/Logo";
import { signOut, fetchCurrentProfile } from "@/services/auth";
import { useTheme, type ThemeMode } from "@/hooks/useTheme";

type AppHeaderProps = {
  showSearch?: boolean;
  showBack?: boolean;
  variant?: "onDark" | "onLight";
};

type Notification = {
  id: number;
  title: string;
  time: string;
  read: boolean;
  icon?: string;
  color?: string;
};

const initialNotifications: Notification[] = [
  { id: 1, title: "Nueva masterclass disponible", time: "Hace 5 min", read: false, icon: "ri-live-line", color: "#A78BFA" },
  { id: 2, title: "Ana, sigue tu racha de 7 días", time: "Hoy 09:00 AM", read: false, icon: "ri-fire-line", color: "#FB923C" },
];

// Menú del perfil: fondo verde menta suave; cada ícono lleva su propio color (paleta Vívelo).
const THEME_CYCLE: Record<ThemeMode, ThemeMode> = { light: "dark", dark: "system", system: "light" };
const THEME_LABEL: Record<ThemeMode, string> = { light: "Claro", dark: "Oscuro", system: "Sistema" };
const THEME_ICON: Record<ThemeMode, string> = { light: "ri-sun-line", dark: "ri-moon-line", system: "ri-contrast-2-line" };
const ITEM_BASE = "w-full text-left px-4 py-2.5 text-sm font-medium flex items-center gap-3 cursor-pointer";
// Menú del perfil: verde menta en Claro, azul claro translúcido en Sistema (en Oscuro usa el fondo oscuro).
const MENU_TONES = {
  mint: {
    panel: "bg-emerald-50 dark:bg-background-100 border-emerald-200 dark:border-background-200",
    item: "hover:bg-emerald-100/70 dark:hover:bg-background-200",
    divider: "border-emerald-200/80 dark:border-background-200",
    btn: "bg-white/80 dark:bg-background-200 border-emerald-200 dark:border-background-300",
    head: "bg-emerald-200/90 dark:bg-background-200 border-emerald-300/70 dark:border-background-300",
  },
  sky: {
    panel: "bg-sky-100/80 backdrop-blur-md dark:bg-background-100 border-sky-200 dark:border-background-200",
    item: "hover:bg-sky-200/60 dark:hover:bg-background-200",
    divider: "border-sky-200/80 dark:border-background-200",
    btn: "bg-white/70 dark:bg-background-200 border-sky-200 dark:border-background-300",
    head: "bg-sky-200/90 dark:bg-background-200 border-sky-300/70 dark:border-background-300",
  },
} as const;
const ICON_CLS = "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0";
const MENU_ITEMS = [
  { to: "/app/perfil", label: "Mi Perfil", icon: "ri-user-3-line", color: "#0F9F76" },
  { to: "/app/resultado", label: "Mi resultado", icon: "ri-sparkling-2-line", color: "#7C3AED" },
  { to: "/app/afiliado", label: "Panel de Afiliado", icon: "ri-team-line", color: "#2563EB" },
  { to: "/app/lider", label: "Panel de Líder", icon: "ri-vip-crown-2-line", color: "#B7791F" },
  { to: "/app/mis-prospectos", label: "Mis Prospectos", icon: "ri-user-search-line", color: "#0F9F76" },
  { to: "/app/comunidad", label: "Comunidad", icon: "ri-community-line", color: "#7C3AED" },
  { to: "/app/retos", label: "Retos", icon: "ri-trophy-line", color: "#EA580C" },
];

export default function AppHeader({ showSearch = false, showBack = false, variant = "onDark" }: AppHeaderProps) {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();
  const cycleTheme = () => setMode(THEME_CYCLE[mode]);
  const tone = MENU_TONES[mode === "system" ? "sky" : "mint"];
  const itemCls = `${ITEM_BASE} ${tone.item}`;
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const [ringing, setRinging] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Sabe si el usuario autenticado es admin, para mostrar (o no) el acceso
  // al Panel Administrador en el menú de perfil.
  useEffect(() => {
    let active = true;
    fetchCurrentProfile().then((profile) => {
      if (active) setIsAdmin(profile?.role === "admin");
    });
    return () => {
      active = false;
    };
  }, []);

  // Cierra ambos menús al cambiar de pantalla (ruta)
  useEffect(() => {
    setMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  // Cierra los menús al tocar o hacer clic en cualquier parte fuera de ellos
  const notifRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen && !notifOpen) return;
    const handlePointer = (event: Event) => {
      const target = event.target as Node | null;
      if (notifOpen && notifRef.current && !notifRef.current.contains(target)) setNotifOpen(false);
      if (menuOpen && menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    return () => document.removeEventListener("pointerdown", handlePointer);
  }, [menuOpen, notifOpen]);

  // Cierra ambos menús al presionar la tecla Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Hace sonar la campanita cuando llega una notificación nueva (aumenta el conteo)
  const prevUnreadRef = useRef(unreadCount);
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setRinging(true);
      const timer = window.setTimeout(() => setRinging(false), 1000);
      prevUnreadRef.current = unreadCount;
      return () => window.clearTimeout(timer);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Demo: simula la llegada de una notificación nueva (una vez por sesión)
  useEffect(() => {
    if (sessionStorage.getItem("vv-notif-demo")) return;
    const timer = window.setTimeout(() => {
      sessionStorage.setItem("vv-notif-demo", "1");
      setNotifications((prev) => [
        { id: Date.now(), title: "¡Desbloqueaste un nuevo logro!", time: "Ahora", read: false, icon: "ri-trophy-line", color: "#FACC15" },
        ...prev,
      ]);
    }, 6000);
    return () => window.clearTimeout(timer);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate("/");
  };

  const isDark = variant === "onDark";
  const wrapperCls = isDark
    ? "bg-background-950 text-foreground-50"
    : "bg-background-50 text-foreground-950 border-b border-background-200";

  return (
    <header className={`relative z-20 vv-safe-x ${wrapperCls}`}>
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 md:px-6 pb-4 pt-[calc(env(safe-area-inset-top)+1rem)]">
        {/* Espacio reservado: el logo ahora es flotante, así que la barra conserva su altura con este hueco */}
        <div className="h-11 w-[11.5rem]" aria-hidden="true"></div>

        {/* Logo + palabra "Vívelo": flotantes y fijos en la esquina superior izquierda (igual que el perfil y el menú) */}
        <div className={`fixed z-40 left-3 md:left-5 top-[calc(env(safe-area-inset-top)+0.625rem)] flex items-center gap-1 rounded-full pl-1.5 pr-4 py-0.5 backdrop-blur-md shadow-[0_4px_16px_rgba(11,31,58,0.18)] border ${isDark ? "bg-background-950/80 border-white/10" : "bg-background-50/85 border-background-200"}`}>
          {showBack ? (
            <button
              onClick={() => navigate(-1)}
              aria-label="Volver"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-800/60 cursor-pointer"
            >
              <i className={`ri-arrow-left-line text-xl ${isDark ? "text-foreground-100" : "text-foreground-800"}`}></i>
            </button>
          ) : null}
          <Logo size="md" variant={isDark ? "onDark" : "onLight"} />
        </div>

        {showSearch ? (
          <div className="hidden md:flex flex-1 max-w-md mx-6">
            <div className="w-full flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2">
              <i className="ri-search-line text-foreground-300 text-sm"></i>
              <input
                type="text"
                placeholder="Buscar cursos..."
                className="w-full bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-400"
              />
            </div>
          </div>
        ) : null}

        {/* Campanita + perfil: flotantes y fijos en la esquina superior derecha (no se mueven al hacer scroll) */}
        <div className={`fixed z-40 right-3 md:right-5 top-[calc(env(safe-area-inset-top)+0.625rem)] flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 backdrop-blur-md shadow-[0_4px_16px_rgba(11,31,58,0.18)] border ${isDark ? "bg-background-950/80 border-white/10" : "bg-background-50/85 border-background-200"}`}>
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => { setMenuOpen(false); setNotifOpen((v) => !v); }}
              aria-label="Notificaciones"
              className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-800/60 cursor-pointer"
            >
              <i className={`ri-notification-3-line text-xl ${isDark ? "text-foreground-100" : "text-foreground-800"} ${ringing ? "vv-bell-ring" : ""}`}></i>
              {unreadCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-950 text-[10px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              ) : null}
            </button>
            <div
              aria-hidden={!notifOpen}
              className={`absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-5rem)] overflow-y-auto origin-top-right rounded-2xl ${tone.panel} text-foreground-950 border shadow-[0_10px_30px_rgba(11,31,58,0.18)] transition-all duration-200 ease-out ${notifOpen ? "opacity-100 visible translate-y-0 scale-100" : "opacity-0 invisible -translate-y-1 scale-95"}`}
            >
              <div className={`sticky top-0 z-10 ${tone.head} backdrop-blur-md px-4 py-3 border-b flex items-center justify-between gap-2`}>
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-sm">Notificaciones</span>
                  {unreadCount > 0 ? <span className="text-[10px] font-bold bg-[#20C997] text-[#0B1F3A] rounded-full px-1.5 py-0.5">{unreadCount} nuevas</span> : null}
                </div>
                {unreadCount > 0 ? (
                  <button onClick={() => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))} className="text-[11px] font-semibold text-emerald-800 dark:text-[#20C997] hover:underline cursor-pointer whitespace-nowrap">
                    Marcar leídas
                  </button>
                ) : null}
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-foreground-600">
                  <i className="ri-notification-off-line text-2xl block mb-1"></i>No tienes notificaciones
                </div>
              ) : (
                <ul className="p-2 flex flex-col gap-1">
                  {notifications.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)))}
                        className={`w-full text-left flex items-start gap-3 rounded-xl px-2.5 py-2.5 ${tone.item} cursor-pointer transition-colors`}
                      >
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ color: n.color ?? "#20C997", backgroundColor: `${n.color ?? "#20C997"}26` }}>
                          <i className={`${n.icon ?? "ri-notification-3-line"} text-lg`}></i>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm leading-snug ${n.read ? "text-foreground-600 font-medium" : "text-foreground-950 font-semibold"}`}>{n.title}</span>
                          <span className="block text-[11px] text-foreground-500 mt-0.5">{n.time}</span>
                        </span>
                        {!n.read ? <span className="w-2 h-2 rounded-full bg-[#20C997] mt-1.5 flex-shrink-0" aria-label="Sin leer"></span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => { setNotifOpen(false); navigate("/app/notificaciones"); }}
                className={`w-full flex items-center justify-center gap-1.5 border-t ${tone.divider} px-4 py-2.5 text-xs font-semibold text-foreground-700 ${tone.item} cursor-pointer`}
              >
                <i className="ri-settings-3-line"></i> Ajustes de avisos
              </button>
            </div>
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setNotifOpen(false); setMenuOpen((v) => !v); }}
              className="flex items-center gap-2 cursor-pointer"
              aria-label="Perfil"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Portrait%20of%20a%20warm%20smiling%20latin%20woman%20in%20her%20late%20twenties%20with%20long%20dark%20hair%2C%20soft%20studio%20light%2C%20confident%20professional%20look%2C%20natural%20makeup%2C%20clean%20plain%20navy%20background%2C%20high%20quality%20editorial%20portrait%20photography%20with%20soft%20shadows&width=120&height=120&seq=vivelo-avatar-ana&orientation=squarish"
                alt="Perfil"
                className="w-9 h-9 rounded-full object-cover object-top border-2 border-primary-500"
              />
              <i className={`ri-arrow-down-s-line text-lg ${isDark ? "text-foreground-100" : "text-foreground-800"} hidden sm:inline`}></i>
            </button>
            <div
              aria-hidden={!menuOpen}
              className={`absolute right-0 mt-2 w-60 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-5rem)] overflow-y-auto origin-top-right rounded-xl ${tone.panel} text-foreground-950 border shadow-[0_10px_30px_rgba(11,31,58,0.18)] transition-all duration-200 ease-out ${menuOpen ? "opacity-100 visible translate-y-0 scale-100" : "opacity-0 invisible -translate-y-1 scale-95"}`}
            >
              {MENU_ITEMS.map((it) => {
                const main = (
                  <button onClick={() => { setMenuOpen(false); navigate(it.to); }} className={it.to === "/app/perfil" ? `${itemCls} flex-1 min-w-0` : itemCls}>
                    <span className={ICON_CLS} style={{ color: it.color, backgroundColor: `${it.color}1F` }}>
                      <i className={`${it.icon} text-base`}></i>
                    </span>
                    {it.label}
                  </button>
                );
                if (it.to !== "/app/perfil") return <div key={it.to}>{main}</div>;
                const next = THEME_CYCLE[mode];
                return (
                  <div key={it.to} className="flex items-center pr-3">
                    {main}
                    <button
                      onClick={cycleTheme}
                      aria-label={`Apariencia: ${THEME_LABEL[mode]}. Toca para cambiar a ${THEME_LABEL[next]}`}
                      title={`Apariencia: ${THEME_LABEL[mode]} · toca para ${THEME_LABEL[next]}`}
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border ${tone.btn} text-[#0B1F3A] dark:text-foreground-100 hover:bg-white cursor-pointer transition-colors`}
                    >
                      <i className={`${THEME_ICON[mode]} text-lg`}></i>
                    </button>
                  </div>
                );
              })}
              {isAdmin ? (
                <button onClick={() => { setMenuOpen(false); navigate("/app/admin"); }} className={`${itemCls} border-t ${tone.divider}`}>
                  <span className={ICON_CLS} style={{ color: "#0B1F3A", backgroundColor: "#0B1F3A1F" }}>
                    <i className="ri-shield-star-line text-base"></i>
                  </span>
                  Panel Administrador
                </button>
              ) : null}
              <button onClick={handleLogout} className={`${itemCls} text-accent-700 border-t ${tone.divider}`}>
                <span className={ICON_CLS} style={{ color: "#B42318", backgroundColor: "#B423181F" }}>
                  <i className="ri-logout-box-r-line text-base"></i>
                </span>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
